import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { formatDateTime } from '../../helpers';
import {
  GET_MY_SESSIONS_QUERY,
  TERMINATE_SESSION_MUTATION,
} from '../../gql';
import {
  Button,
  FieldLabel,
  Paper,
  Pill,
  SurfaceTextField,
} from '../../components/design-system';

interface AccountSession {
  createdAt: string;
  current: boolean;
  deviceName?: string;
  expiresAt?: string;
  id: string;
  kind: 'BROWSER' | 'MOBILE';
  lastUsedAt?: string;
  userAgent?: string;
}

interface SessionRowProps {
  onTerminate: (session: AccountSession) => void;
  session: AccountSession;
}

const emptyStyles = { p: 4, textAlign: 'center' };
const tableContainerStyles = { borderRadius: 2, p: 0 };
const tableStyles = { minWidth: 650 };
const deviceStyles = { alignItems: 'center', gap: 2 };
const deviceIconStyles = { color: 'text.secondary' };
const secondaryTextStyles = { color: 'text.secondary' };
const dialogContentStyles = { minWidth: { sm: 420 }, pt: 1 };
const dialogFieldStyles = { gap: 0.75, mt: 2 };

export function getBrowserName(userAgent?: string): string | undefined {
  if (!userAgent) {
    return undefined;
  }

  if (/Edg\//.test(userAgent)) {
    return 'Microsoft Edge';
  }

  if (/OPR\//.test(userAgent)) {
    return 'Opera';
  }

  if (/Firefox\//.test(userAgent)) {
    return 'Firefox';
  }

  if (/Chrome\//.test(userAgent)) {
    return 'Google Chrome';
  }

  if (/Safari\//.test(userAgent)) {
    return 'Safari';
  }

  return undefined;
}

const SessionRow = React.memo(({ onTerminate, session }: SessionRowProps) => {
  const intl = useIntl();
  const handleTerminate = useCallback(() => {
    onTerminate(session);
  }, [onTerminate, session]);
  const isMobile = session.kind === 'MOBILE';
  const deviceName = isMobile
    ? session.deviceName ||
      intl.formatMessage({
        id: 'account.security.mobileApp',
        defaultMessage: 'Mobile app',
      })
    : getBrowserName(session.userAgent) ||
      intl.formatMessage({
        id: 'account.security.browser',
        defaultMessage: 'Web browser',
      });
  const deviceType = isMobile
    ? intl.formatMessage({
        id: 'account.security.mobile',
        defaultMessage: 'Mobile',
      })
    : intl.formatMessage({
        id: 'account.security.browserType',
        defaultMessage: 'Browser',
      });

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" sx={deviceStyles}>
          {isMobile ? (
            <PhoneIphoneOutlinedIcon sx={deviceIconStyles} />
          ) : (
            <ComputerOutlinedIcon sx={deviceIconStyles} />
          )}
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight={600}>
                {deviceName}
              </Typography>
              {session.current && (
                <Pill tone="success">
                  {intl.formatMessage({
                    id: 'account.security.current',
                    defaultMessage: 'Current',
                  })}
                </Pill>
              )}
            </Stack>
            <Typography variant="caption" sx={secondaryTextStyles}>
              {deviceType}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell>{formatDateTime(session.createdAt)}</TableCell>
      <TableCell>
        {session.lastUsedAt
          ? formatDateTime(session.lastUsedAt)
          : intl.formatMessage({
              id: 'account.security.notAvailable',
              defaultMessage: 'Not available',
            })}
      </TableCell>
      <TableCell align="right">
        <Button
          density="compact"
          onClick={handleTerminate}
          tone="danger"
          variant="outlined"
        >
          {intl.formatMessage({
            id: 'account.security.terminate',
            defaultMessage: 'Terminate',
          })}
        </Button>
      </TableCell>
    </TableRow>
  );
});

SessionRow.displayName = 'SessionRow';

const SecuritySettings = React.memo(() => {
  const intl = useIntl();
  const { data, error, loading, refetch } = useQuery(GET_MY_SESSIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [terminateSession, { loading: terminating }] = useMutation(
    TERMINATE_SESSION_MUTATION
  );
  const [selectedSession, setSelectedSession] = useState<AccountSession | null>(
    null
  );
  const [password, setPassword] = useState('');
  const [dialogError, setDialogError] = useState<string>();
  const sessions = useMemo<AccountSession[]>(
    () => data?.getMySessions ?? [],
    [data]
  );
  const handleTerminateOpen = useCallback((session: AccountSession) => {
    setSelectedSession(session);
    setPassword('');
    setDialogError(undefined);
  }, []);
  const handleDialogClose = useCallback(() => {
    if (!terminating) {
      setSelectedSession(null);
    }
  }, [terminating]);
  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
      setDialogError(undefined);
    },
    []
  );
  const handleConfirm = useCallback(async () => {
    if (!selectedSession) {
      return;
    }

    try {
      const result = await terminateSession({
        variables: { id: selectedSession.id, password },
      });
      const payload = result.data?.terminateSession;
      const validationError = payload?.errors?.[0]?.error;

      if (validationError) {
        setDialogError(validationError);

        return;
      }

      if (!payload?.result) {
        throw new Error('Session was not terminated.');
      }

      if (selectedSession.current) {
        window.location.assign('/signin');

        return;
      }

      await refetch();
      setSelectedSession(null);
    } catch {
      setDialogError(
        intl.formatMessage({
          id: 'account.security.terminateError',
          defaultMessage: 'The session could not be terminated.',
        })
      );
    }
  }, [intl, password, refetch, selectedSession, terminateSession]);

  if (loading && !data) {
    return (
      <Typography color="text.secondary">
        {intl.formatMessage({
          id: 'general.loading',
          defaultMessage: 'Loading…',
        })}
      </Typography>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {intl.formatMessage({
          id: 'account.security.loadError',
          defaultMessage: 'Unable to load active sessions.',
        })}
      </Alert>
    );
  }

  return (
    <>
      {sessions.length === 0 ? (
        <Paper sx={emptyStyles}>
          <Typography color="text.secondary" variant="body2">
            {intl.formatMessage({
              id: 'account.security.empty',
              defaultMessage: 'No active sessions found.',
            })}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={tableContainerStyles}>
          <Table
            aria-label={intl.formatMessage({
              id: 'account.security.sessionsTable',
              defaultMessage: 'Active sessions',
            })}
            sx={tableStyles}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  {intl.formatMessage({
                    id: 'account.security.device',
                    defaultMessage: 'Device',
                  })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({
                    id: 'account.security.created',
                    defaultMessage: 'Created',
                  })}
                </TableCell>
                <TableCell>
                  {intl.formatMessage({
                    id: 'account.security.lastActive',
                    defaultMessage: 'Last active',
                  })}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <SessionRow
                  key={session.id}
                  onTerminate={handleTerminateOpen}
                  session={session}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={selectedSession != null} onClose={handleDialogClose}>
        <DialogTitle>
          {intl.formatMessage({
            id: 'account.security.confirmTitle',
            defaultMessage: 'Terminate session?',
          })}
        </DialogTitle>
        <DialogContent sx={dialogContentStyles}>
          <DialogContentText>
            {intl.formatMessage({
              id: 'account.security.confirmDescription',
              defaultMessage:
                'Enter your current password to sign this session out.',
            })}
          </DialogContentText>
          <Stack sx={dialogFieldStyles}>
            <FieldLabel htmlFor="terminate-session-password">
              {intl.formatMessage({
                id: 'account.security.password',
                defaultMessage: 'Current password',
              })}
            </FieldLabel>
            <SurfaceTextField
              autoComplete="current-password"
              autoFocus
              error={Boolean(dialogError)}
              helperText={dialogError}
              id="terminate-session-password"
              onChange={handlePasswordChange}
              surface="sunken"
              type="password"
              value={password}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={terminating}
            onClick={handleDialogClose}
            tone="neutral"
            variant="text"
          >
            {intl.formatMessage({
              id: 'general.cancel',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button
            disabled={terminating || password.length === 0}
            onClick={handleConfirm}
            tone="danger"
          >
            {intl.formatMessage({
              id: 'account.security.terminate',
              defaultMessage: 'Terminate',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

SecuritySettings.displayName = 'SecuritySettings';

export { SecuritySettings };
